import type { AudioChunk, AudioChunkSource, PcmSource } from '../../visual/index.js';

/**
 * The decoded song, presented to the exporter as PCM.
 *
 * The browser side of `PcmSource`, and deliberately thin. The song is already
 * decoded and sitting in an `AudioBuffer` because that is what the player is
 * playing; this reads from that buffer rather than making a second copy of it.
 * On a four-minute stereo track at 48 kHz a copy would be 92 MB, and there is no
 * reason to hold two.
 *
 * `copyFromChannel` is used rather than `getChannelData` on purpose.
 * `getChannelData` hands back the buffer's own storage, and a caller who then
 * kept or mutated the returned array would be editing the song the player is
 * about to play. `copyFromChannel` copies exactly the window asked for, into
 * memory the exporter owns.
 */
export class AudioBufferPcmSource implements PcmSource {
  constructor(private readonly buffer: AudioBuffer) {}

  get sampleRate(): number {
    return this.buffer.sampleRate;
  }

  get channelCount(): number {
    return this.buffer.numberOfChannels;
  }

  get frameCount(): number {
    return this.buffer.length;
  }

  get duration(): number {
    return this.buffer.duration;
  }

  read(startFrame: number, frameCount: number, destination: readonly Float32Array[]): void {
    const channels = this.buffer.numberOfChannels;
    const available = Math.max(0, Math.min(frameCount, this.buffer.length - startFrame));

    for (let channel = 0; channel < channels; channel += 1) {
      const target = destination[channel];
      if (target === undefined) continue;
      // Zeroed first: the last chunk of a song is usually shorter than the
      // window, and whatever the previous chunk left in the scratch buffer
      // would otherwise be encoded as a repeat of audio from earlier in the
      // track — a very audible bug at the exact moment a listener is paying
      // attention to the ending.
      target.fill(0, 0, frameCount);
      if (available <= 0) continue;
      // Through the wider type: `copyFromChannel` is declared over a
      // `Float32Array<ArrayBuffer>` while a subarray carries the looser
      // `ArrayBufferLike`, and the two do not unify even though the memory is
      // exactly what the call wants.
      this.buffer.copyFromChannel(
        target.subarray(0, available) as Float32Array<ArrayBuffer>,
        channel,
        startFrame,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Chunks
// ---------------------------------------------------------------------------

interface AudioDataNative {
  close(): void;
}

interface AudioDataConstructor {
  new (init: {
    format: string;
    sampleRate: number;
    numberOfFrames: number;
    numberOfChannels: number;
    timestamp: number;
    data: Float32Array;
  }): AudioDataNative;
}

function audioDataGlobal(): AudioDataConstructor | undefined {
  return (globalThis as unknown as { AudioData?: AudioDataConstructor }).AudioData;
}

/** Whether this browser can build the audio objects an encoder takes. */
export function audioDataAvailable(): boolean {
  return typeof audioDataGlobal() === 'function';
}

/** An `AudioData` on its way to the encoder, and the promise to release it. */
export interface NativeAudioChunk extends AudioChunk {
  readonly native: AudioDataNative;
}

export interface AudioDataChunkSourceOptions {
  readonly pcm: PcmSource;
  /** The largest chunk that will ever be asked for. Sizes the scratch buffer. */
  readonly maxChunkFrames: number;
}

/**
 * Turns windows of PCM into `AudioData` for the encoder.
 *
 * One scratch buffer, reused for every chunk. `AudioData` copies what it is
 * given at construction, so the same memory can back every chunk of the song —
 * which is what keeps a long export's audio path flat rather than proportional
 * to the track length.
 *
 * The layout is `f32-planar`: channels laid end to end in a single array, which
 * is the format WebCodecs takes and the one the buffer is already in. Any
 * interleaved format would mean a second pass over every sample for no gain.
 */
export class AudioDataChunkSource implements AudioChunkSource {
  private readonly scratch: Float32Array;
  private readonly views: Float32Array[];
  private open = 0;
  private disposed = false;

  constructor(private readonly options: AudioDataChunkSourceOptions) {
    const channels = Math.max(1, options.pcm.channelCount);
    const frames = Math.max(1, options.maxChunkFrames);
    this.scratch = new Float32Array(channels * frames);
    this.views = [];
    for (let channel = 0; channel < channels; channel += 1) {
      this.views.push(this.scratch.subarray(channel * frames, (channel + 1) * frames));
    }
  }

  get sampleRate(): number {
    return this.options.pcm.sampleRate;
  }

  get channelCount(): number {
    return Math.max(1, this.options.pcm.channelCount);
  }

  get liveChunks(): number {
    return this.open;
  }

  take(index: number, startFrame: number, frameCount: number): AudioChunk | null {
    if (this.disposed) return null;
    const AudioDataCtor = audioDataGlobal();
    if (AudioDataCtor === undefined) return null;

    const channels = this.channelCount;
    const sampleRate = this.options.pcm.sampleRate;
    this.options.pcm.read(startFrame, frameCount, this.views);

    // The timestamp comes from the sample index, never from a counter that
    // advances per chunk. Same rule as the video side, same reason.
    const timestampMicros = Math.round((startFrame * 1_000_000) / sampleRate);
    const durationMicros =
      Math.round(((startFrame + frameCount) * 1_000_000) / sampleRate) - timestampMicros;

    // A partial final chunk is packed tight rather than padded out. Handing the
    // encoder a full-length window with silence on the end would add real
    // silence to the file and make the audio track longer than the song.
    const stride = this.views[0]?.length ?? frameCount;
    let data: Float32Array;
    if (frameCount === stride) {
      data = this.scratch;
    } else {
      data = new Float32Array(channels * frameCount);
      for (let channel = 0; channel < channels; channel += 1) {
        const view = this.views[channel];
        if (view === undefined) continue;
        data.set(view.subarray(0, frameCount), channel * frameCount);
      }
    }

    let native: AudioDataNative;
    try {
      native = new AudioDataCtor({
        format: 'f32-planar',
        sampleRate,
        numberOfFrames: frameCount,
        numberOfChannels: channels,
        timestamp: timestampMicros,
        data,
      });
    } catch {
      // A refused allocation reads as a dropped chunk, which the session counts
      // and reports, rather than as an exception mid-export.
      return null;
    }

    this.open += 1;
    let closed = false;
    const chunk: NativeAudioChunk = {
      index,
      startFrame,
      frameCount,
      timestampMicros,
      durationMicros,
      native,
      close: (): void => {
        if (closed) return;
        closed = true;
        this.open -= 1;
        try {
          native.close();
        } catch {
          // Already closed by the encoder path.
        }
      },
    };
    return chunk;
  }

  dispose(): void {
    this.disposed = true;
  }
}
